import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import WorkOrderFractionationDetails from "./fractionation/WorkOrderFractionationDetails";
import { WORK_ORDERS_URL } from "constants/urls";
import useCrud from "hooks/useCrud";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { MdExpandMore } from "react-icons/md";
import { IMovementWorkOrderDetail, IMovementWorkOrderSummary } from "types";

export type WorkOrderMovementHandle = {
  getDetail: () => Promise<IMovementWorkOrderDetail | null>;
};

type Props = {
  workOrder: IMovementWorkOrderSummary | null | undefined;
};

const getWorkOrderTypeLabel = (type: string | undefined) => {
  switch (type) {
    case "fractionation":
      return "Fraccionado";
    default:
      return "Orden de trabajo";
  }
};

const renderWorkOrderDetails = (order: IMovementWorkOrderDetail) => {
  switch (order.work_order_type) {
    case "fractionation":
      return <WorkOrderFractionationDetails order={order} />;
    default:
      return <Typography>Este tipo de orden todavía no tiene una vista administrativa.</Typography>;
  }
};

const WorkOrderMovement = forwardRef<WorkOrderMovementHandle, Props>(
  function WorkOrderMovement({ workOrder }, ref) {
    const [expanded, setExpanded] = useState(false);
    const [detail, setDetail] = useState<IMovementWorkOrderDetail | null>(null);
    const [error, setError] = useState("");
    const pendingRequest = useRef<Promise<IMovementWorkOrderDetail | null> | null>(null);
    const { fetchData: fetchWorkOrder, loading } = useCrud<IMovementWorkOrderDetail>(
      WORK_ORDERS_URL,
      { disableAutoFetch: true },
    );

    const loadDetail = useCallback(async () => {
      if (detail) return detail;
      if (pendingRequest.current) return pendingRequest.current;
      if (!workOrder?.id) {
        setError("No se encontró la orden de trabajo del movimiento.");
        return null;
      }

      setError("");
      const request = (async () => {
        try {
          const response = await fetchWorkOrder(String(workOrder.id), true);
          if (response.status !== 200 || !response.data) {
            setError(response.data?.message ?? "No se pudo cargar la orden.");
            return null;
          }
          const loadedDetail = response.data as IMovementWorkOrderDetail;
          setDetail(loadedDetail);
          return loadedDetail;
        } catch {
          setError("No se pudo cargar la orden.");
          return null;
        } finally {
          pendingRequest.current = null;
        }
      })();
      pendingRequest.current = request;
      return request;
    }, [detail, fetchWorkOrder, workOrder?.id]);

    useImperativeHandle(ref, () => ({
      getDetail: async () => {
        const loadedDetail = await loadDetail();
        if (!loadedDetail) {
          setExpanded(true);
        }
        return loadedDetail;
      },
    }), [loadDetail]);

    return (
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => {
          setExpanded(isExpanded);
          if (isExpanded) void loadDetail();
        }}
      >
        <AccordionSummary expandIcon={<MdExpandMore />}>
          <Typography variant="body2">
            <b>{getWorkOrderTypeLabel(workOrder?.work_order_type)}</b>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {loading ? (
            <Typography role="status">Cargando orden de trabajo…</Typography>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : detail ? (
            renderWorkOrderDetails(detail)
          ) : null}
        </AccordionDetails>
      </Accordion>
    );
  },
);

export default WorkOrderMovement;
